import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiVs, FiPlus, FiX, FiCheck, FiMinus } = FiIcons;

function ComparisonTool() {
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [showComparison, setShowComparison] = useState(false);

  const availableVehicles = [
    {
      id: 1,
      make: 'Audi',
      model: 'Q5 Quattro',
      year: 2023,
      price: 54999,
      mpg: '23/29',
      horsepower: 261,
      safety: 5,
      image: 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=300&h=200&fit=crop'
    },
    {
      id: 2,
      make: 'BMW',
      model: 'X3 xDrive',
      year: 2022,
      price: 48999,
      mpg: '25/31',
      horsepower: 248,
      safety: 5,
      image: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=300&h=200&fit=crop'
    },
    {
      id: 3,
      make: 'Mercedes',
      model: 'GLC 300',
      year: 2023,
      price: 52999,
      mpg: '22/28',
      horsepower: 255,
      safety: 5,
      image: 'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?w=300&h=200&fit=crop'
    }
  ];

  const addVehicle = (vehicle) => {
    if (selectedVehicles.length < 3 && !selectedVehicles.find(v => v.id === vehicle.id)) {
      setSelectedVehicles([...selectedVehicles, vehicle]);
    }
  };

  const removeVehicle = (vehicleId) => {
    setSelectedVehicles(selectedVehicles.filter(v => v.id !== vehicleId));
  };

  const compareFeatures = [
    { key: 'price', label: 'Price (CAD)', format: (val) => `$${val.toLocaleString()}` },
    { key: 'mpg', label: 'Fuel Economy', format: (val) => `${val} L/100km` },
    { key: 'horsepower', label: 'Horsepower', format: (val) => `${val} HP` },
    { key: 'safety', label: 'Safety Rating', format: (val) => `${val}/5 Stars` }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Halifax Vehicle Comparison Tool
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Compare up to 3 vehicles side-by-side to find the perfect match for Maritime driving conditions.
          </p>
        </motion.div>

        {/* Vehicle Selection */}
        {!showComparison && (
          <div className="space-y-8">
            <div className="text-center">
              <h3 className="text-xl font-semibold mb-4">Select Vehicles to Compare</h3>
              <p className="text-gray-600 mb-6">Choose 2-3 vehicles from our Halifax inventory</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {availableVehicles.map((vehicle) => {
                const isSelected = selectedVehicles.find(v => v.id === vehicle.id);
                return (
                  <motion.div
                    key={vehicle.id}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6 }}
                    className={`border-2 rounded-xl overflow-hidden transition-all ${
                      isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <img
                      src={vehicle.image}
                      alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                      className="w-full h-32 object-cover"
                    />
                    <div className="p-4">
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </h4>
                      <p className="text-blue-600 font-bold mb-3">
                        ${vehicle.price.toLocaleString()} CAD
                      </p>
                      <button
                        onClick={() => isSelected ? removeVehicle(vehicle.id) : addVehicle(vehicle)}
                        disabled={!isSelected && selectedVehicles.length >= 3}
                        className={`w-full px-4 py-2 rounded-lg transition-colors ${
                          isSelected
                            ? 'bg-red-100 text-red-600 hover:bg-red-200'
                            : selectedVehicles.length >= 3
                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                      >
                        {isSelected ? (
                          <>
                            <SafeIcon icon={FiMinus} className="w-4 h-4 inline mr-2" />
                            Remove
                          </>
                        ) : (
                          <>
                            <SafeIcon icon={FiPlus} className="w-4 h-4 inline mr-2" />
                            {selectedVehicles.length >= 3 ? 'Max Selected' : 'Add to Compare'}
                          </>
                        )}
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {selectedVehicles.length >= 2 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <button
                  onClick={() => setShowComparison(true)}
                  className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center space-x-2 mx-auto"
                >
                  <SafeIcon icon={FiVs} className="w-5 h-5" />
                  <span>Compare Selected Vehicles ({selectedVehicles.length})</span>
                </button>
              </motion.div>
            )}
          </div>
        )}

        {/* Comparison Table */}
        {showComparison && selectedVehicles.length >= 2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <div className="flex justify-between items-center">
              <h3 className="text-2xl font-bold">Halifax Vehicle Comparison</h3>
              <button
                onClick={() => setShowComparison(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                Back to Selection
              </button>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-4 font-semibold">Features</th>
                      {selectedVehicles.map((vehicle) => (
                        <th key={vehicle.id} className="text-center p-4">
                          <div className="space-y-2">
                            <img
                              src={vehicle.image}
                              alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                              className="w-16 h-12 object-cover rounded mx-auto"
                            />
                            <div className="font-semibold text-sm">
                              {vehicle.year} {vehicle.make} {vehicle.model}
                            </div>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {compareFeatures.map((feature) => (
                      <tr key={feature.key} className="border-t border-gray-100">
                        <td className="p-4 font-medium text-gray-900">{feature.label}</td>
                        {selectedVehicles.map((vehicle) => (
                          <td key={vehicle.id} className="p-4 text-center">
                            {feature.format(vehicle[feature.key])}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="border-t border-gray-100 bg-blue-50">
                      <td className="p-4 font-medium text-gray-900">Halifax Winter Ready</td>
                      {selectedVehicles.map((vehicle) => (
                        <td key={vehicle.id} className="p-4 text-center">
                          <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mx-auto" />
                        </td>
                      ))}
                    </tr>
                    <tr className="border-t border-gray-100">
                      <td className="p-4 font-medium text-gray-900">Maritime Climate Ready</td>
                      {selectedVehicles.map((vehicle) => (
                        <td key={vehicle.id} className="p-4 text-center">
                          <SafeIcon icon={FiCheck} className="w-5 h-5 text-green-500 mx-auto" />
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* AI Recommendation */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6">
              <h4 className="font-semibold mb-3 flex items-center space-x-2">
                <SafeIcon icon={FiVs} className="w-5 h-5 text-blue-600" />
                <span>Halifax AI Recommendation</span>
              </h4>
              <p className="text-gray-700 mb-4">
                Based on Halifax driving conditions and Maritime climate considerations, here's our AI analysis:
              </p>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  For Halifax winters, the <strong>{selectedVehicles[0]?.make} {selectedVehicles[0]?.model}</strong> offers 
                  the best combination of all-wheel drive capability, fuel efficiency, and value. Its advanced traction 
                  systems are particularly well-suited for Maritime weather conditions, while the comprehensive warranty 
                  provides peace of mind for Nova Scotia drivers.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-4 justify-center">
              {selectedVehicles.map((vehicle) => (
                <button
                  key={vehicle.id}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Schedule Test Drive - {vehicle.make} {vehicle.model}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
}

export default ComparisonTool;